import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { listDomainAssessments } from '../../lib/api';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { aggregateOrgSpend } from '../../lib/cost-truth';
import { usd } from '../../lib/graph-visuals';
import { UnbilledCloudsNote } from '../UnbilledCloudsNote';

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/**
 * The org-wide cloud-spend headline (one box). Accumulates every domain the user can
 * access into two figures: the RECORDED cost so far this month (billed where we have
 * it, estimate only where we don't) and the projected full-month RUN RATE. Billed
 * cost carries no pill — it's the default; we flag "Estimated" only when we're not
 * yet sure (some domain has no billed signal). This is cost, not the billing fee, so
 * every role sees it.
 */
export function OrgSpend() {
  const assessments = useQuery({
    queryKey: ['domain-assessments'],
    queryFn: listDomainAssessments,
    refetchInterval: 60_000,
    // Fresh for a full interval so a remount doesn't re-fetch (and briefly re-derive a
    // different number); keep the last value during background refetches so the spend
    // figure never flashes to a transient/estimate value before settling.
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  if (assessments.isLoading) {
    return (
      <Card className="p-5">
        <Skeleton className="h-20 w-full" />
      </Card>
    );
  }

  const rows = assessments.data ?? [];
  const { total, projectedTotal, provenance, unbilledClouds } = aggregateOrgSpend(rows);
  const estimated = provenance === 'estimated';

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
            Total monthly cloud spend
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            Across {plural(rows.length, 'domain')} you can access
          </p>
        </div>
        {estimated && (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-hairline px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-muted"
            title="Some domains have no billed signal yet, so part of this is our reconciled estimate."
          >
            <span className="h-1.5 w-1.5 rounded-full bg-muted" aria-hidden />
            Estimated
          </span>
        )}
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <div className="font-display text-3xl font-extrabold text-bone tnum">{usd(total)}</div>
          <div className="mt-0.5 text-xs font-medium text-muted">Recorded cost to date</div>
        </div>
        <div>
          <div className="font-display text-3xl font-extrabold text-bone tnum">
            {usd(projectedTotal)}
          </div>
          <div className="mt-0.5 text-xs font-medium text-muted">Run rate for monthly total</div>
        </div>
      </div>
      {!estimated && <UnbilledCloudsNote clouds={unbilledClouds} className="mt-4" />}
    </Card>
  );
}
